import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  'https://api.rwfngo.com/api';

/** Normalize list responses: `{ success, data: [] }`, `{ data: [] }`, or bare array. */
export function extractApiDataList(response) {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    const headers = config.headers;
    if (headers && typeof headers.set === 'function') {
      headers.set('Content-Type', false);
    } else if (headers) {
      delete headers['Content-Type'];
      delete headers['content-type'];
    }
  }
  return config;
});

// Build query params: omit undefined, null, and empty string so filters are sent correctly
function buildQueryParams(params) {
  const cleaned = {};
  Object.keys(params).forEach((key) => {
    const v = params[key];
    if (v !== undefined && v !== null && v !== '') cleaned[key] = v;
  });
  return cleaned;
}

// User APIs
export const userService = {
  getAllUsers: (params = {}) => {
    const q = buildQueryParams(params);
    return apiClient.get('/users', { params: q });
  },
  getUserById: (id) => apiClient.get(`/users/${id}`),
  createUser: (data) => apiClient.post('/users', data),
  updateUser: (id, data) => apiClient.put(`/users/${id}`, data),
  deleteUser: (id) => apiClient.delete(`/users/${id}`),
};

// Campaign APIs
export const campaignService = {
  getAllCampaigns: (params = {}) => {
    const q = buildQueryParams(params);
    return apiClient.get('/campaigns', { params: q });
  },
  getCampaignById: (id) => apiClient.get(`/campaigns/${id}`),
  getCampaignDocuments: (id) => apiClient.get(`/campaigns/${id}/documents`),
  uploadCampaignDocument: (id, formData) => apiClient.post(`/campaigns/${id}/documents`, formData),
  deleteCampaignDocument: (campaignId, docId) => apiClient.delete(`/campaigns/${campaignId}/documents/${docId}`),
  createCampaign: (data) => apiClient.post('/campaigns', data),
  createCampaignWithFile: (formData) => apiClient.post('/campaigns', formData),
  updateCampaign: (id, data) => apiClient.put(`/campaigns/${id}`, data),
  updateCampaignWithFile: (id, formData) => apiClient.post(`/campaigns/${id}`, formData),
  updateCampaignStatus: (id, data) => apiClient.put(`/campaigns/${id}/status`, data),
  getCampaignProducts: (campaignId) => apiClient.get(`/campaigns/${campaignId}/products`),
  createCampaignProduct: (campaignId, data) => apiClient.post(`/campaigns/${campaignId}/products`, data),
  updateCampaignProduct: (campaignId, productId, data) =>
    apiClient.put(`/campaigns/${campaignId}/products/${productId}`, data),
  deleteCampaignProduct: (campaignId, productId) =>
    apiClient.delete(`/campaigns/${campaignId}/products/${productId}`),
  getCampaignEssentials: (campaignId) =>
    apiClient.get(`/campaigns/${campaignId}/essentials`, { params: { include_inactive: 1 } }),
  createCampaignEssential: (campaignId, data) => apiClient.post(`/campaigns/${campaignId}/essentials`, data),
  updateCampaignEssential: (campaignId, essentialId, data) =>
    apiClient.put(`/campaigns/${campaignId}/essentials/${essentialId}`, data),
  deleteCampaignEssential: (campaignId, essentialId) =>
    apiClient.delete(`/campaigns/${campaignId}/essentials/${essentialId}`),
};

export const campaignRatingService = {
  getAll: (params = {}) => {
    const q = buildQueryParams(params);
    return apiClient.get('/ratings', { params: q });
  },
  getApprovedByCampaign: (campaignId) => apiClient.get(`/campaigns/${campaignId}/ratings`),
  updateStatus: (id, data) => apiClient.put(`/ratings/${id}/status`, data),
  delete: (id) => apiClient.delete(`/ratings/${id}`),
};

export const siteStatService = {
  getAll: (params = {}) => {
    const q = buildQueryParams(params);
    return apiClient.get('/site-stats', { params: q });
  },
  create: (data) => apiClient.post('/site-stats', data),
  update: (id, data) => apiClient.put(`/site-stats/${id}`, data),
  delete: (id) => apiClient.delete(`/site-stats/${id}`),
};

export const testimonialService = {
  getAll: (params = {}) => {
    const q = buildQueryParams(params);
    return apiClient.get('/testimonials', { params: q });
  },
  updateSummary: (data) => apiClient.put('/testimonials/summary', data),
  create: (formData) => apiClient.post('/testimonials', formData),
  update: (id, formData) => apiClient.post(`/testimonials/${id}`, formData),
  delete: (id) => apiClient.delete(`/testimonials/${id}`),
};

export const newsletterService = {
  getAll: (params = {}) => {
    const q = buildQueryParams(params);
    return apiClient.get('/newsletters', { params: q });
  },
  delete: (id) => apiClient.delete(`/newsletters/${id}`),
};

export const legalPageService = {
  getAll: () => apiClient.get('/legal-pages'),
  getBySlug: (slug) => apiClient.get(`/legal-pages/${slug}`),
  update: (slug, data) => apiClient.put(`/legal-pages/${slug}`, data),
};

export const financialDocumentService = {
  getAll: (params = {}) => {
    const q = buildQueryParams(params);
    return apiClient.get('/financial-documents', { params: q });
  },
  create: (formData) => apiClient.post('/financial-documents', formData),
  update: (id, formData) => apiClient.post(`/financial-documents/${id}`, formData),
  delete: (id) => apiClient.delete(`/financial-documents/${id}`),
};

export const teamMemberService = {
  getAll: (params = {}) => {
    const q = buildQueryParams(params);
    return apiClient.get('/team-members', { params: q });
  },
  getById: (id) => apiClient.get(`/team-members/${id}`),
  create: (formData) => apiClient.post('/team-members', formData),
  update: (id, formData) => apiClient.post(`/team-members/${id}`, formData),
  delete: (id) => apiClient.delete(`/team-members/${id}`),
};

export const homePopupService = {
  getAll: (params = {}) => {
    const q = buildQueryParams(params);
    return apiClient.get('/home-popups', { params: q });
  },
  getById: (id) => apiClient.get(`/home-popups/${id}`),
  create: (formData) => apiClient.post('/home-popups', formData),
  update: (id, formData) => apiClient.post(`/home-popups/${id}`, formData),
  delete: (id) => apiClient.delete(`/home-popups/${id}`),
};

export const homeSliderService = {
  getAll: (params = {}) => {
    const q = buildQueryParams(params);
    return apiClient.get('/home-sliders', { params: q });
  },
  getById: (id) => apiClient.get(`/home-sliders/${id}`),
  create: (formData) => apiClient.post('/home-sliders', formData),
  update: (id, formData) => apiClient.post(`/home-sliders/${id}`, formData),
  delete: (id) => apiClient.delete(`/home-sliders/${id}`),
};

export const campaignCategoryService = {
  getAll: (params = {}) => {
    const q = buildQueryParams(params);
    return apiClient.get('/campaign-categories', { params: q });
  },
  getById: (id) => apiClient.get(`/campaign-categories/${id}`),
  create: (data) => apiClient.post('/campaign-categories', data),
  update: (id, data) => apiClient.put(`/campaign-categories/${id}`, data),
  delete: (id) => apiClient.delete(`/campaign-categories/${id}`),
};

// Donation APIs
export const donationService = {
  getAllDonations: (params = {}) => {
    const q = buildQueryParams(params);
    return apiClient.get('/donations', { params: q });
  },
  getDonationsByUser: (userId) => apiClient.get(`/donations/user/${userId}`),
  getDonationById: (id) => apiClient.get(`/donations/${id}`),
  getReceiptPdf: (id) => {
    const token = localStorage.getItem('adminToken');
    return apiClient.get(`/donations/${id}/receipt`, {
      responseType: 'blob',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },
  getRecurringDonations: () => apiClient.get('/donations/recurring'),
  cancelRecurringDonation: (id) => apiClient.put(`/donations/${id}/cancel-recurring`),
  updateRecurringDonation: (id, data) => apiClient.put(`/donations/${id}/recurring`, data),
  getRecurringStats: () => apiClient.get('/donations/stats/recurring'),
};

// Volunteer APIs
export const volunteerService = {
  getAllApplications: (params = {}) => {
    const q = buildQueryParams(params);
    return apiClient.get('/volunteers', { params: q });
  },
  updateApplicationStatus: (id, status) => apiClient.put(`/volunteers/${id}/status`, { status }),
};

// Contact APIs
export const contactService = {
  getAllInquiries: (params = {}) => {
    const q = buildQueryParams(params);
    return apiClient.get('/contact', { params: q });
  },
  respondToInquiry: (id, response) => apiClient.put(`/contact/${id}/respond`, { response }),
};

// Gallery APIs
export const galleryService = {
  getAllGallery: (params = {}) => {
    const q = buildQueryParams(params);
    return apiClient.get('/gallery', { params: q });
  },
  getGalleryById: (id) => apiClient.get(`/gallery/${id}`),
  addGalleryItem: (data) => apiClient.post('/gallery', data),
  updateGalleryItem: (id, data) => apiClient.put(`/gallery/${id}`, data),
  deleteGalleryItem: (id) => apiClient.delete(`/gallery/${id}`),
  uploadGalleryImages: (formData) => apiClient.post('/gallery/upload', formData),
};

// Awards APIs
export const awardService = {
  getAllAwards: () => apiClient.get('/awards'),
  createAward: (data) => apiClient.post('/awards', data),
  updateAward: (id, data) => apiClient.put(`/awards/${id}`, data),
  deleteAward: (id) => apiClient.delete(`/awards/${id}`),
};

// CSR APIs
export const csrService = {
  getAllActivities: () => apiClient.get('/csr'),
  createActivity: (data) => apiClient.post('/csr', data),
  updateActivity: (id, data) => apiClient.put(`/csr/${id}`, data),
};

// Report APIs
export const reportService = {
  getAllReports: (params = {}) => {
    const q = buildQueryParams(params);
    return apiClient.get('/reports', { params: q });
  },
  getReportById: (id) => apiClient.get(`/reports/${id}`),
  getAnalyticsOverview: () => apiClient.get('/reports/analytics/overview'),
  deleteReport: (id) => apiClient.delete(`/reports/${id}`),
};

// Sub Admin APIs
export const subAdminService = {
  getAllSubAdmins: (params = {}) => {
    const q = buildQueryParams(params);
    return apiClient.get('/sub-admins', { params: q });
  },
  getSubAdminById: (id) => apiClient.get(`/sub-admins/${id}`),
  createSubAdmin: (data) => apiClient.post('/sub-admins', data),
  updateSubAdmin: (id, data) => apiClient.put(`/sub-admins/${id}`, data),
  deleteSubAdmin: (id) => apiClient.delete(`/sub-admins/${id}`),
};

export default apiClient;
